import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { PersonMinimalFormPayload } from '@/forms/PersonMinimalForm/types';
import { Classification } from '@/types/classification';
import type { LeadSource } from '@/types/leadSource';
import type PaginatedResponse from '@/types/paginatedResponse';
import type { PaymentTypeChoice, Person, PersonMinimal } from '@/types/person';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';
import { errorMessage } from '@/utils/mapAxiosErrorMessages';

import {
  GetOneTimeTokenResponse,
  MyPeopleParams,
  PeopleParams,
  PersonMfaStatus,
  TimeZoneResponse,
  SSIEligibilityDetails
} from './types';

export const personRepository = {
  async getPersons({
    host,
    params
  }: {
    host: string;
    params: MyPeopleParams;
  }): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
    try {
      const path = `/api/dugout/people/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params: { ...params, my_people: false }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get assigned people.');
    }
  },

  async getMyPeople({
    host,
    params
  }: {
    host: string;
    params: MyPeopleParams;
  }): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
    return this.getPeople({ host, params: { ...params, my_people: true } });
  },
  async getPeople({
    host,
    params
  }: {
    host: string;
    params: PeopleParams;
  }): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
    try {
      const path = `/api/dugout/people/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params: { ...params }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get assigned people.');
    }
  },

  async getPersonProfile({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<Person>> {
    try {
      const path = `/api/dugout/people/${personId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get profile data: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async getPersonProfileByHealthieId({
    host,
    healthieId
  }: {
    host: string;
    healthieId: string;
  }): Promise<RepositoryResponse<Person>> {
    try {
      const path = `/api/dugout/people/by_healthie_id/${healthieId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get profile data with provided Healthie ID: ${
          (error as AxiosError).message
        }`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async getPersonProfileByKustomerId({
    host,
    kustomerId
  }: {
    host: string;
    kustomerId: string;
  }): Promise<RepositoryResponse<Person>> {
    try {
      const path = `/api/dugout/people/by_kustomer_id/${kustomerId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get profile data with provided Kustomer ID: ${
          (error as AxiosError).message
        }`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async getPersonProfileByCandidPatientId({
    host,
    candidPatientId
  }: {
    host: string;
    candidPatientId: string;
  }): Promise<RepositoryResponse<Person>> {
    try {
      const path = `/api/dugout/people/by_candid_patient_id/${candidPatientId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get profile data with provided Candid patient ID: ${
          (error as AxiosError).message
        }`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async createPersonMinimal({
    host,
    values
  }: {
    host: string;
    values: PersonMinimalFormPayload;
  }): Promise<PersonMinimal> {
    const path = '/api/dugout/people/';
    const response = await ajax.post<PersonMinimalFormPayload, AxiosResponse>({
      url: `${host}${path}`,
      data: values
    });

    return response.data;
  },

  async updatePerson({
    host,
    personId,
    person
  }: {
    host: string;
    personId: string;
    person: Partial<Person>;
  }): Promise<RepositoryResponse<Person>> {
    try {
      const path = `/api/dugout/people/${personId}/`;
      const response = await ajax.patch({
        url: `${host}${path}`,
        data: person
      });

      return {
        data: response.data as Person,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to update person.');
    }
  },

  async getTimeZones({ host }: { host: string }): Promise<RepositoryResponse<TimeZoneResponse>> {
    try {
      const response = await ajax.get({
        url: `${host}/api/time_zones/`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get time zones.');
    }
  },

  async getPaymentTypes({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<PaymentTypeChoice[]>> {
    try {
      const response = await ajax.get({
        url: `${host}/api/dugout/people/payment_types/`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get payment types.');
    }
  },

  async getWarmingSequences({ host }: { host: string }): Promise<[number, string][] | void> {
    try {
      const response = await ajax.get({
        url: `${host}/api/dugout/people/warming_sequence/`
      });

      return response.data;
    } catch (error) {
      await Promise.reject(
        `Failed to get warming sequence choices. ${errorMessage(error as AxiosError)}`
      );
    }
  },

  async getLeadSources({ host }: { host: string }): Promise<LeadSource[]> {
    try {
      const response = await ajax.get({
        url: `${host}/api/lead_sources/`
      });

      return response.data;
    } catch (error) {
      throw new Error(`Failed to get lead source choices. ${errorMessage(error as AxiosError)}`);
    }
  },

  async createHealthieAccount({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/create_healthie_account/`,
      data: {}
    });

    return true;
  },

  async createCandidPatient({ host, personId }: { host: string; personId: string }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/create_candid_patient/`,
      data: {}
    });

    return true;
  },

  async deleteFutureAppointments({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/delete_future_appointments/`,
      data: {}
    });

    return true;
  },

  async sendAppInvite({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<{ message: string }> {
    const response = await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/send_app_invite/`,
      data: {}
    });

    return response.data;
  },

  async sendOneTimeLoginEmail({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<boolean> {
    const response = await ajax.post<object, AxiosResponse<boolean>>({
      url: `${host}/api/dugout/one-time-login/${personId}/email/`,
      data: {},
      config: {
        withCredentials: true,
        withXSRFToken: true,
        xsrfCookieName: 'csrftoken',
        xsrfHeaderName: 'X-CSRFToken'
      }
    });
    return response.data;
  },

  async runPostSaveTasks({ host, personId }: { host: string; personId: string }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/run_post_save_tasks/`,
      data: {}
    });

    return true;
  },

  async syncWithHealthie({ host, personId }: { host: string; personId: string }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/sync_with_healthie/`,
      data: {}
    });

    return true;
  },

  async fetchUpcomingAppointments({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<true> {
    await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/people/${personId}/fetch_upcoming_appointments/`,
      data: {}
    });

    return true;
  },

  async impersonate({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<GetOneTimeTokenResponse>> {
    try {
      const url = `${host}/api/users/auth/impersonate`;

      const response = await ajax.post({
        url,
        data: { person_id: personId }
      });
      return {
        data: {
          one_time_token: '',
          uid: '',
          link: '',
          ...response.data
        },
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to impersonate user.');
    }
  },

  async getMfaStatus({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<PersonMfaStatus> {
    const response = await ajax.get({
      url: `${host}/api/users/auth/mfa/people/${personId}/`
    });
    return response.data;
  },

  async resetMfa({
    host,
    personId,
    reason
  }: {
    host: string;
    personId: string;
    reason: string;
  }): Promise<void> {
    await ajax.post({
      url: `${host}/api/users/auth/mfa/people/${personId}/`,
      data: { reason }
    });
  },

  async getSSIEligibilityDetails({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<SSIEligibilityDetails>> {
    try {
      const response = await ajax.get({
        url: `${host}/api/dugout/people/${personId}/ssi_eligibility_details/`
      });
      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get SSI eligibility details.');
    }
  },

  async getPersonClassifications({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<Classification[]>> {
    try {
      const response = await ajax.get({
        url: `${host}/api/dugout/people/${personId}/classifications/`
      });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get classifications.');
    }
  }
};

const PersonRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getPersons(
      params: MyPeopleParams
    ): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
      return personRepository.getPersons({ host, params });
    },

    async getMyPeople(
      params: MyPeopleParams
    ): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
      return personRepository.getMyPeople({ host, params });
    },
    async getPeople(
      params: PeopleParams
    ): Promise<RepositoryResponse<PaginatedResponse<PersonMinimal[]>>> {
      return personRepository.getPeople({ host, params });
    },

    async getPersonProfile({
      personId
    }: {
      personId: string;
    }): Promise<RepositoryResponse<Person>> {
      return personRepository.getPersonProfile({ host, personId });
    },

    async getPersonProfileByHealthieId({
      healthieId
    }: {
      healthieId: string;
    }): Promise<RepositoryResponse<Person>> {
      return personRepository.getPersonProfileByHealthieId({ host, healthieId });
    },

    async getPersonProfileByKustomerId({
      kustomerId
    }: {
      kustomerId: string;
    }): Promise<RepositoryResponse<Person>> {
      return personRepository.getPersonProfileByKustomerId({ host, kustomerId });
    },

    async getPersonProfileByCandidPatientId({
      candidPatientId
    }: {
      candidPatientId: string;
    }): Promise<RepositoryResponse<Person>> {
      return personRepository.getPersonProfileByCandidPatientId({ host, candidPatientId });
    },

    async createPersonMinimal({
      values
    }: {
      values: PersonMinimalFormPayload;
    }): Promise<PersonMinimal> {
      return personRepository.createPersonMinimal({ host, values });
    },

    async updatePerson({
      id,
      values
    }: {
      id: string;
      values: Partial<Person>;
    }): Promise<RepositoryResponse<Person>> {
      return personRepository.updatePerson({ host, personId: id, person: values });
    },

    async getTimeZones(): Promise<RepositoryResponse<TimeZoneResponse>> {
      return personRepository.getTimeZones({ host });
    },

    async getPaymentTypes(): Promise<RepositoryResponse<PaymentTypeChoice[]>> {
      return personRepository.getPaymentTypes({ host });
    },

    async getWarmingSequences(): Promise<[number, string][] | void> {
      return personRepository.getWarmingSequences({ host });
    },

    async getLeadSources(): Promise<LeadSource[]> {
      return personRepository.getLeadSources({ host });
    },

    async createHealthieAccount(personId: string): Promise<true> {
      return personRepository.createHealthieAccount({ host, personId });
    },

    async createCandidPatient(personId: string): Promise<true> {
      return personRepository.createCandidPatient({ host, personId });
    },

    async deleteFutureAppointments(personId: string): Promise<true> {
      return personRepository.deleteFutureAppointments({ host, personId });
    },

    async sendAppInvite(personId: string): Promise<{ message: string }> {
      return personRepository.sendAppInvite({ host, personId });
    },

    async sendOneTimeLoginEmail(personId: string): Promise<boolean> {
      return personRepository.sendOneTimeLoginEmail({ host, personId });
    },

    async runPostSaveTasks(personId: string): Promise<true> {
      return personRepository.runPostSaveTasks({ host, personId });
    },

    async syncWithHealthie(personId: string): Promise<true> {
      return personRepository.syncWithHealthie({ host, personId });
    },

    async fetchUpcomingAppointments(personId: string): Promise<true> {
      return personRepository.fetchUpcomingAppointments({ host, personId });
    },
    async impersonate(personId: string): Promise<RepositoryResponse<GetOneTimeTokenResponse>> {
      return personRepository.impersonate({ host, personId });
    },
    async getMfaStatus(personId: string): Promise<PersonMfaStatus> {
      return personRepository.getMfaStatus({ host, personId });
    },
    async resetMfa(personId: string, reason: string): Promise<void> {
      return personRepository.resetMfa({ host, personId, reason });
    },
    async getSSIEligibilityDetails(
      personId: string
    ): Promise<RepositoryResponse<SSIEligibilityDetails>> {
      return personRepository.getSSIEligibilityDetails({ host, personId });
    },

    async getPersonClassifications(
      personId: string
    ): Promise<RepositoryResponse<Classification[]>> {
      return personRepository.getPersonClassifications({ host, personId });
    }
  };
};

export default PersonRepository;
